/**
 * Jungle Berry Wishlist
 * localStorage-based wishlist with heart icon toggling on product cards,
 * header icon with count badge, and a dedicated wishlist page renderer.
 */

(function () {
  'use strict';

  var STORAGE_KEY = 'jb_wishlist';

  /* ── Helpers ─────────────────────────────────────────────────── */

  function getWishlist() {
    try {
      return JSON.parse(localStorage.getItem(STORAGE_KEY)) || [];
    } catch (e) {
      return [];
    }
  }

  function saveWishlist(list) {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(list));
    } catch (e) {}
  }

  function isWishlisted(productId) {
    return getWishlist().some(function (item) {
      return String(item.id) === String(productId);
    });
  }

  function addToWishlist(productId, productData) {
    var list = getWishlist();
    if (
      !list.some(function (item) {
        return String(item.id) === String(productId);
      })
    ) {
      list.push(Object.assign({ id: String(productId) }, productData));
      saveWishlist(list);
    }
  }

  function removeFromWishlist(productId) {
    var list = getWishlist().filter(function (item) {
      return String(item.id) !== String(productId);
    });
    saveWishlist(list);
  }

  /* ── Sync all heart buttons to current wishlist state ─────────── */

  function syncButtons() {
    document.querySelectorAll('[data-wishlist-btn]').forEach(function (btn) {
      var id = btn.getAttribute('data-product-id');
      if (isWishlisted(id)) {
        btn.classList.add('is-wishlisted');
        btn.setAttribute('aria-pressed', 'true');
        btn.setAttribute('aria-label', 'Remove from wishlist');
      } else {
        btn.classList.remove('is-wishlisted');
        btn.setAttribute('aria-pressed', 'false');
        btn.setAttribute('aria-label', 'Add to wishlist');
      }
    });
    updateHeaderCount();
  }

  /* ── Header count badge ───────────────────────────────────────── */

  function updateHeaderCount() {
    var count = getWishlist().length;
    document.querySelectorAll('[data-wishlist-count]').forEach(function (el) {
      el.textContent = count > 0 ? count : '';
      el.style.display = count > 0 ? 'flex' : 'none';
    });
    // Also toggle the "has items" class on the header link
    document
      .querySelectorAll('[data-wishlist-header-link]')
      .forEach(function (el) {
        if (count > 0) {
          el.classList.add('wishlist-header__has-items');
        } else {
          el.classList.remove('wishlist-header__has-items');
        }
      });
  }

  /* ── Toggle handler ──────────────────────────────────────────── */

  function handleToggle(e) {
    var btn = e.currentTarget;
    var productId = btn.getAttribute('data-product-id');
    if (!productId) return;

    e.preventDefault();
    e.stopPropagation();

    if (isWishlisted(productId)) {
      removeFromWishlist(productId);
    } else {
      // Gather product data from data attributes on the button
      var productData = {
        title: btn.getAttribute('data-product-title') || '',
        url: btn.getAttribute('data-product-url') || '',
        image: btn.getAttribute('data-product-image') || '',
        price: btn.getAttribute('data-product-price') || '',
        comparePrice: btn.getAttribute('data-product-compare-price') || '',
        description: btn.getAttribute('data-product-description') || '',
        vendor: btn.getAttribute('data-product-vendor') || '',
        handle: btn.getAttribute('data-product-handle') || '',
        singleVariant: btn.getAttribute('data-single-variant') === 'true',
      };
      addToWishlist(productId, productData);
    }
    syncButtons();

    // Brief "pop" animation feedback
    btn.classList.add('wishlist-btn--animate');
    setTimeout(function () {
      btn.classList.remove('wishlist-btn--animate');
    }, 400);
  }

  /* ── Attach listeners to buttons ────────────────────────────── */

  function attachListeners() {
    document.querySelectorAll('[data-wishlist-btn]').forEach(function (btn) {
      if (btn.getAttribute('data-wishlist-init')) return;
      btn.setAttribute('data-wishlist-init', '1');
      btn.addEventListener('click', handleToggle);
    });
  }

  /* ── Wishlist page renderer ──────────────────────────────────── */

  var heartSvg =
    '<svg aria-hidden="true" focusable="false" role="presentation" class="icon icon-heart" viewBox="0 0 24 21"><path d="M16.663.004a6.5 6.5 0 00-4.31 1.9L12 2.254l-.353-.352a6.501 6.501 0 00-9.194 9.194l8.84 8.84a1 1 0 001.414 0l8.84-8.84A6.5 6.5 0 0016.95-.002l-.287.006zm.287 1.994A4.5 4.5 0 0121.452 6.5l-.006.238a4.5 4.5 0 01-1.313 2.945L12 17.815 3.867 9.683a4.501 4.501 0 016.366-6.366l1.06 1.06a1 1 0 001.414 0l1.06-1.06a4.5 4.5 0 013.183-1.319z"/></svg>';
  var removeSvg =
    '<svg aria-hidden="true" focusable="false" role="presentation" viewBox="0 0 24 24" width="16" height="16"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 01-2 2H8a2 2 0 01-2-2L5 6"/><path d="M10 11v6M14 11v6"/><path d="M9 6V4a1 1 0 011-1h4a1 1 0 011 1v2"/></svg>';

  function stripHtml(str) {
    if (typeof str !== 'string') return '';
    return str.replace(/<[^>]*>/g, '');
  }

  function renderWishlistPage() {
    var grid = document.querySelector('[data-wishlist-grid]');
    var emptyMsg = document.querySelector('[data-wishlist-empty]');
    if (!grid) return;

    var list = getWishlist();
    grid.innerHTML = '';

    if (list.length === 0) {
      if (emptyMsg) emptyMsg.style.display = 'block';
      return;
    }
    if (emptyMsg) emptyMsg.style.display = 'none';

    list.forEach(function (item) {
      var price = escapeHtml(stripHtml(item.price || ''));
      var comparePrice = escapeHtml(stripHtml(item.comparePrice || ''));
      var desc = escapeHtml(item.description || '');

      var priceHtml = comparePrice
        ? '<span class="wishlist-card__price-sale">' +
          price +
          '</span>' +
          '<s class="wishlist-card__price-compare">' +
          comparePrice +
          '</s>'
        : '<span class="wishlist-card__price-sale">' + price + '</span>';

      var ctaHtml = item.singleVariant
        ? '<a class="wishlist-card__atc btn" href="' +
          escapeHtml(item.url) +
          '">ADD TO BAG</a>'
        : '<a class="wishlist-card__atc btn" href="' +
          escapeHtml(item.url) +
          '">SELECT SIZE</a>';

      var el = document.createElement('div');
      el.className = 'wishlist-card';
      el.setAttribute('data-wishlist-card-id', String(item.id));

      el.innerHTML = [
        '<div class="wishlist-card__image-wrap">',
        item.image
          ? '<a href="' +
            escapeHtml(item.url) +
            '" class="wishlist-card__img-link">' +
            '<img src="' +
            escapeHtml(item.image) +
            '" alt="' +
            escapeHtml(item.title) +
            '" loading="lazy" width="600" height="750">' +
            '</a>'
          : '<div class="wishlist-card__img-placeholder"></div>',
        '<button type="button"',
        ' class="wishlist-btn is-wishlisted wishlist-card__heart"',
        ' data-wishlist-btn',
        ' data-product-id="' + escapeHtml(String(item.id)) + '"',
        ' data-product-title="' + escapeHtml(item.title || '') + '"',
        ' data-product-url="' + escapeHtml(item.url || '') + '"',
        ' data-product-image="' + escapeHtml(item.image || '') + '"',
        ' data-product-price="' + escapeHtml(stripHtml(item.price || '')) + '"',
        ' data-product-compare-price="' +
          escapeHtml(stripHtml(item.comparePrice || '')) +
          '"',
        ' data-product-description="' +
          escapeHtml(item.description || '') +
          '"',
        ' data-product-vendor="' + escapeHtml(item.vendor || '') + '"',
        ' data-product-handle="' + escapeHtml(item.handle || '') + '"',
        ' data-single-variant="' +
          (item.singleVariant ? 'true' : 'false') +
          '"',
        ' aria-label="Remove from wishlist" aria-pressed="true">',
        heartSvg,
        '</button>',
        '</div>',
        '<div class="wishlist-card__body">',
        '<a class="wishlist-card__title" href="' +
          escapeHtml(item.url) +
          '">' +
          escapeHtml(item.title) +
          '</a>',
        desc ? '<p class="wishlist-card__desc">' + desc + '</p>' : '',
        '<div class="wishlist-card__price-row">' + priceHtml + '</div>',
        ctaHtml,
        '<button type="button" class="wishlist-card__remove" data-wishlist-remove-id="' +
          escapeHtml(String(item.id)) +
          '">' +
          removeSvg +
          '<span>Remove</span></button>',
        '</div>',
      ].join('');

      grid.appendChild(el);
    });

    // Wire up remove buttons
    grid.querySelectorAll('[data-wishlist-remove-id]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        var id = btn.getAttribute('data-wishlist-remove-id');
        removeFromWishlist(id);
        renderWishlistPage();
        updateHeaderCount();
      });
    });
  }

  function escapeHtml(str) {
    if (typeof str !== 'string') return '';
    return str
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  /* ── MutationObserver to pick up dynamically injected cards ─── */

  var _observer = null;

  function observeDOM() {
    _observer = new MutationObserver(function () {
      // Disconnect before making any DOM changes to prevent an infinite loop.
      // syncButtons() sets textContent on the count badge, which is a childList
      // mutation and would re-fire this callback without the disconnect guard.
      _observer.disconnect();
      attachListeners();
      syncButtons();
      _observer.observe(document.body, { childList: true, subtree: true });
    });
    _observer.observe(document.body, { childList: true, subtree: true });
  }

  /* ── Init ────────────────────────────────────────────────────── */

  function init() {
    attachListeners();
    syncButtons();
    renderWishlistPage();
    observeDOM();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
